import { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { SKIP_BIN_LANDINGS } from "@/lib/skipBinLandings";
import { api } from "@/lib/api";
import { ArrowRight, CheckCircle2, Container, Clock, ShieldCheck, MapPin, Info } from "lucide-react";

const AUD = (n) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(Math.round(n));

function LocalBusinessSchema({ suburb, sizes }) {
  const offers = (sizes || []).map((s) => ({
    "@type": "Offer",
    "name": `${s.label} skip bin hire in ${suburb.name}`,
    "priceCurrency": "AUD",
    "price": s.min,
    "priceValidUntil": new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
    "availability": "https://schema.org/InStock",
  }));
  const data = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `Site Cleanup — Skip Bin Hire ${suburb.name}`,
    "url": `https://sitecleanup.com.au/skip-bins-${suburb.slug}`,
    "email": "sitecleanup@imperiumtech.com.au",
    "description": suburb.metaDesc,
    "areaServed": { "@type": "Place", "name": `${suburb.name}, VIC, Australia` },
    "serviceType": "Skip Bin Hire",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": suburb.name,
      "addressRegion": "VIC",
      "addressCountry": "AU",
    },
    "makesOffer": offers,
  };
  const safe = JSON.stringify(data).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safe }} />;
}

function FAQSchema({ faq }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  const safe = JSON.stringify(data).replace(/</g, "\\u003c").replace(/>/g, "\\u003e").replace(/&/g, "\\u0026");
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: safe }} />;
}

export default function SkipBinLanding() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/skip-bins-/, "").replace(/\/$/, "");
  const suburb = slug && SKIP_BIN_LANDINGS[slug];
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get("/skip-bin-rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  if (!suburb) return <Navigate to="/service-areas" replace />;
  const path = `/skip-bins-${suburb.slug}`;

  return (
    <PublicLayout>
      <Seo title={suburb.metaTitle} description={suburb.metaDesc} path={path} />
      {rates?.sizes && <LocalBusinessSchema suburb={suburb} sizes={rates.sizes} />}
      <FAQSchema faq={suburb.faq} />

      {/* HERO */}
      <section className="bg-asphalt text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 rs-diag opacity-25" />
        <div className="rs-container relative">
          <div className="flex items-center gap-2 text-xs text-white/60 uppercase tracking-widest" data-testid={`skipbin-breadcrumb-${suburb.slug}`}>
            <Link to="/" className="hover:text-safety">Home</Link>
            <span>/</span>
            <span className="text-white/85">Skip Bin Hire {suburb.name}</span>
          </div>
          <span className="rs-eyebrow text-safety mt-6 block flex items-center gap-2"><Container size={14} /> Skip Bin Hire · {suburb.name}, VIC</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase font-display leading-[0.95] max-w-4xl" data-testid={`skipbin-h1-${suburb.slug}`}>
            {suburb.h1}
          </h1>
          <p className="mt-6 max-w-3xl text-white/80 text-base md:text-lg leading-relaxed">
            {suburb.intro[0]}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/request-quote" className="rs-btn-primary" data-testid={`skipbin-cta-hero-${suburb.slug}`}>
              Request a skip bin quote <ArrowRight size={16} className="ml-2" />
            </Link>
            <Link to="/services" className="rs-btn-outline-light">Explore all services</Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-safety" /> GST-inclusive, no hidden fees</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-safety" /> Delivery in 24–72 hours</span>
            <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-safety" /> Vetted local operator</span>
          </div>
        </div>
      </section>

      {/* INTRO CONTINUED */}
      <section className="py-20 bg-white">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">Local coverage</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">Why {suburb.name} chooses us</h2>
          </div>
          <div className="md:col-span-2 space-y-4 text-asphalt text-base leading-relaxed">
            {suburb.intro.slice(1).map((p, i) => (<p key={i}>{p}</p>))}
          </div>
        </div>
      </section>

      {/* SIZES TABLE */}
      <section className="py-20 bg-concrete border-y border-border">
        <div className="rs-container">
          <div className="max-w-2xl">
            <span className="rs-eyebrow">Sizes & pricing</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">Skip bin sizes in {suburb.name}</h2>
            <p className="mt-4 text-muted-foreground">Six industry-standard sizes covering everything from a one-room clean-out through to a full builder's project. Prices are GST-inclusive, standard 7-day hire.</p>
          </div>
          {rates?.sizes ? (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {rates.sizes.map((s) => (
                <div key={s.code} className="rs-card h-full flex flex-col" data-testid={`skipbin-size-${suburb.slug}-${s.code}`}>
                  <span className="w-11 h-11 grid place-items-center bg-safety/10 text-safety">
                    <Container size={20} />
                  </span>
                  <p className="mt-4 font-display uppercase text-lg leading-tight">{s.label}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{s.capacity}</p>
                  <p className="mt-3 font-display text-2xl text-asphalt">{AUD(s.min)} <span className="text-safety">–</span> {AUD(s.max)}</p>
                  <p className="mt-2 text-sm text-muted-foreground italic flex-1">{s.best_for}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-6 text-sm text-muted-foreground">Loading current {suburb.name} skip bin sizes…</p>
          )}
          {rates && (
            <p className="mt-6 text-xs text-muted-foreground flex items-center gap-2">
              <Info size={13} className="text-safety" /> Extra days beyond {rates.hire_days}: {AUD(rates.extra_day_fee_min)}–{AUD(rates.extra_day_fee_max)}/day.
            </p>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="rs-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="rs-eyebrow">FAQ · {suburb.name}</span>
            <h2 className="mt-3 text-3xl md:text-4xl uppercase leading-tight font-display">Skip bin questions we hear from {suburb.name}</h2>
            <p className="mt-4 text-muted-foreground">Anything else on your mind? Add it to the "message" field on the quote form and we'll answer with your written quote.</p>
          </div>
          <div className="md:col-span-2 space-y-4">
            {suburb.faq.map((f, i) => (
              <div key={i} className="border-l-2 border-safety pl-5 py-2" data-testid={`skipbin-faq-${suburb.slug}-${i}`}>
                <p className="font-display uppercase text-base tracking-wide">{f.q}</p>
                <p className="mt-2 text-sm text-asphalt leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-safety text-white">
        <div className="rs-container grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="rs-eyebrow text-white/80">Ready when you are</span>
            <h2 className="mt-3 text-3xl md:text-5xl uppercase font-display leading-tight">Free {suburb.name} skip bin quote.</h2>
            <p className="mt-3 text-white/90 max-w-lg">Tell us your address, size and waste type. We'll come back within one business day with a matched local operator and a firm written quote — GST included.</p>
            <div className="mt-6 flex items-center gap-2 text-white/80 text-sm">
              <MapPin size={14} /> Covering {suburb.name} & surrounding suburbs
            </div>
          </div>
          <div className="md:justify-self-end">
            <Link
              to="/request-quote"
              className="inline-flex items-center gap-2 bg-asphalt text-white px-8 py-4 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors"
              data-testid={`skipbin-cta-final-${suburb.slug}`}
            >
              Request a quote <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
